import { type KeyboardEvent, type ReactNode, useState } from 'react'
import { Button, ComboboxPrimitive, Modal, toastManager } from '@systemeio/components'
import { PlusCircleIcon, Link01Icon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { AccessDenied } from 'shared/errors/access-denied'
import { BadRequest } from 'shared/errors/bad-request'
import { ConflictError } from 'shared/errors/conflict-error'
import { InternalError } from 'shared/errors/internal-error'
import useUser from 'shared/hooks/use-user'
import { copyTextToClipboard } from 'shared/utils/copy-to-clipboard'
import { isValidEmail } from 'shared/utils/email-validation'
import type { InviteMembersResponseStatus } from 'modules/community/types/member-interface'
import { useInviteMembers } from '../../api/use-invite-members'

function InviteMembers({
  communityPath,
  isPrivate,
}: {
  communityPath: string
  isPrivate: boolean
}): ReactNode {
  const { t } = useTranslation()
  const { isAccessRestricted } = useUser()

  const [opened, setOpened] = useState(false)
  const [arrEmails, setArrEmails] = useState<string[]>([])
  const [email, setEmail] = useState<string>('')
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const { mutate: inviteMembers, isPending } = useInviteMembers({
    communityPath,
  })

  const handleInviteResult: Record<InviteMembersResponseStatus, () => void> = {
    success: () => {
      toastManager.add({ type: 'success', title: t('members.invite_success_toast') })
      setEmail('')
      setArrEmails([])
      setEmailError('')
      setOpened(false)
    },
    alreadyMember: () => {
      toastManager.add({ type: 'error', title: t('members.invite_already_member_toast') })
    },
    allAlreadyMembers: () => {
      toastManager.add({ type: 'error', title: t('members.invite_all_already_members_toast') })
    },
    partialSuccessWithExistingMembers: () => {
      toastManager.add({ type: 'success', title: t('members.invite_partial_success_toast') })
      setEmail('')
      setArrEmails([])
      setEmailError('')
      setOpened(false)
    },
  }

  const addEmail = (candidateEmail: string): void => {
    if (!candidateEmail) {
      return
    }

    if (arrEmails.includes(candidateEmail)) {
      setEmailError(`${candidateEmail} ${t('members.duplicate_email_error')}`)
      return
    }

    if (!isValidEmail(candidateEmail)) {
      setEmailError(t('members.not_a_valid_email_error'))
      return
    }

    setArrEmails([...arrEmails, candidateEmail])
    setEmail('')
    setEmailError('')
  }

  const handleEmailKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    ;(
      event as KeyboardEvent<HTMLInputElement> & { preventBaseUIHandler?: () => void }
    ).preventBaseUIHandler?.()
    addEmail(event.currentTarget.value)
  }

  return (
    <div data-testid="invite-members-root" className="w-full md:w-auto">
      <Button
        variant="primary"
        data-testid="invite-members-open-button"
        onClick={() => {
          setOpened(true)
        }}
        disabled={isAccessRestricted}
        className="w-full md:w-auto"
      >
        <PlusCircleIcon className="size-5" />
        {t('members.invite_new_members_button.title')}
      </Button>
      <Modal.Root
        open={opened}
        onOpenChange={value => {
          if (!value) {
            setOpened(false)
            setEmailError('')
            setError('')
          }
        }}
      >
        <Modal.Popup>
          <Modal.Header>
            <Modal.Title>{t('modal.invite_member.title')}</Modal.Title>
          </Modal.Header>
          <div className="px-6 pb-6 lg:px-10 lg:pb-10">
            <ComboboxPrimitive.Root<string, true>
              multiple
              value={arrEmails}
              inputValue={email}
              onInputValueChange={(next, details) => {
                if (details.reason === 'input-clear') {
                  addEmail(email)
                  return
                }
                if (error) {
                  setError('')
                }
                setEmail(next)
              }}
              onValueChange={nextEmails => {
                setArrEmails(nextEmails ?? [])
              }}
            >
              <ComboboxPrimitive.InputGroup
                className="mb-6"
                data-invalid={Boolean(error || emailError) || undefined}
              >
                <ComboboxPrimitive.Chips mode="expanded">
                  {arrEmails.map(invitedEmail => (
                    <ComboboxPrimitive.Chip key={invitedEmail} id={invitedEmail}>
                      {invitedEmail}
                      <ComboboxPrimitive.ChipRemove aria-label={t('global.remove')} />
                    </ComboboxPrimitive.Chip>
                  ))}
                  <ComboboxPrimitive.Input
                    type="email"
                    data-testid="invite-members-email-input"
                    onKeyDown={handleEmailKeyDown}
                  />
                </ComboboxPrimitive.Chips>
              </ComboboxPrimitive.InputGroup>
              {(emailError || error) && (
                <ComboboxPrimitive.Error>{emailError || error}</ComboboxPrimitive.Error>
              )}
            </ComboboxPrimitive.Root>
            <div className="mb-6 flex gap-3">
              <Button
                variant="primary"
                data-testid="invite-members-submit-button"
                onClick={() => {
                  if (submitting || isPending) {
                    return
                  }
                  setSubmitting(true)
                  inviteMembers(
                    { emails: arrEmails },
                    {
                      onSuccess: response => {
                        setSubmitting(false)
                        const handler = handleInviteResult[response.status]
                        if (handler) {
                          handler()
                        } else {
                          toastManager.add({
                            type: 'error',
                            title: t('core.error.internal_error_message'),
                          })
                        }
                      },
                      onError: inviteError => {
                        setSubmitting(false)
                        if (inviteError instanceof BadRequest) {
                          setEmailError(inviteError.errors.fields?.emails.join('\n'))
                        } else if (inviteError instanceof InternalError) {
                          setError(t('core.error.internal_error_message'))
                        } else if (inviteError instanceof AccessDenied) {
                          toastManager.add({
                            type: 'error',
                            title: t('core.error.access_denied_error_message'),
                          })
                        } else if (inviteError instanceof ConflictError) {
                          toastManager.add({
                            type: 'error',
                            title: t('core.error.conflict_error_message'),
                          })
                        } else {
                          toastManager.add({
                            type: 'error',
                            title: t('core.error.internal_error_message'),
                          })
                        }
                      },
                    },
                  )
                }}
                disabled={arrEmails.length === 0}
                isLoading={isPending || submitting}
              >
                <PlusCircleIcon className="size-5" />
                {t('modal.invite_member.submit_title')}
              </Button>
              {!isPrivate && (
                <Button
                  variant="secondary"
                  data-testid="invite-members-copy-link-button"
                  className="whitespace-normal!"
                  onClick={e => {
                    e.preventDefault()
                    copyTextToClipboard(
                      `${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://${globalThis.location.host}/community/${communityPath}/join`,
                    )
                    toastManager.add({ type: 'success', title: t('global.copy_clipboard') })
                    setOpened(false)
                  }}
                >
                  <Link01Icon className="size-5" />
                  {t('modal.invite_member.copy_link_to_clipboard')}
                </Button>
              )}
              {error && <p className="mt-2 text-sm text-text-error">{error}</p>}
            </div>
          </div>
        </Modal.Popup>
      </Modal.Root>
    </div>
  )
}

export default InviteMembers
